import {
  Component,
  OnInit,
  OnDestroy,
  Optional,
  Inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject, forkJoin, of, takeUntil, catchError } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { TemplatesService } from '../templates.service';
import { AppService } from '../../../../app.service';
import { AdminFactoryService } from '../../../../global/services/admin-factory.service';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { JsonObjectFactoryService } from '../../../../global/services/json-object-factory.service';
import { UserAssignModalComponent } from '../../../../users-component/user-assign-modal/user-assign-modal.component';

import { safeJsonInput } from '../../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-template-edit',
    templateUrl: './template-edit.component.html',
    styleUrls: ['./template-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TemplateEditComponent implements OnInit, OnDestroy {
  loading = false;
  crudLoading = false;
  minimise = false;
  submitted = false;
  title = 'Create Template';
  mode: 'new' | 'edit' = 'new';

  stepNumber = 1;
  step1Title = 'Create Template';
  step2Title = 'Assign Modules';
  step3Flag = false;
  saveLabel = 'Save & Continue';
  userStatusMessage = '';

  slideStep1Div = true;
  slideStep2Div = false;

  template_data: any = {};
  templateTypes: any[] = [];
  clientJurisdictions: any[] = [];
  templateGroups: any = {};
  clients: any[] = [];
  selectedClientName = '';
  screenModeNew = true;

  rowPrimaryKey: any = 0;
  userId = '';

  // Step 2 — module assignment
  userAssignNav: any[] = [];
  filteredUserAssignNav: any[] = [];
  moduleColumns: any[][] = [[], []];
  selectedApplication: any = null;
  totalModulesForSelectedClient = 0;
  totalModulesAssigned = 0;
  searchTerm = '';
  allExpanded = false;

  private rowData: any = {};
  private destroy$ = new Subject<void>();

  private get clientKey(): number | string {
    return this.appService.USER_SETTINGS?.user?.client_key
      ?? this.appService.USER_SETTINGS?.user?.client?.client_key
      ?? sessionStorage.getItem('client_key')
      ?? '';
  }

  constructor(
    @Optional() public dialogRef: MatDialogRef<TemplateEditComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private templatesService: TemplatesService,
    private appService: AppService,
    private adminFactory: AdminFactoryService,
    private snackbar: GtwSnackbarService,
    private jsonObjFactory: JsonObjectFactoryService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {
    this.rowData = data?.rowData || {};
    this.mode = data?.mode || (this.rowData?.TEMPLATE_KEY ? 'edit' : 'new');
  }

  ngOnInit(): void {
    this.initUserData();
    this.loadTemplateGroups();

    if (this.mode === 'edit') {
      this.initEditMode(this.rowData.TEMPLATE_KEY);
    } else {
      this.initNewMode();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Initialization ───────────────────────────────────────────────

  private initUserData(): void {
    const user = this.appService.USER_SETTINGS?.user;
    if (user) {
      this.clients = user.clients || [];
      this.userId = user.sso_id || '';
      this.template_data.client_key = user.client_key ?? user.client?.client_key ?? '';
      this.selectedClientName = user.client?.client_name || '';
    }
  }

  private initNewMode(): void {
    this.screenModeNew = true;
    this.mode = 'new';
    this.title = 'Create Template';
    this.step1Title = 'Create Template';
    this.step2Title = 'Assign Modules';
    this.template_data.template_active = 1;
    this.saveLabel = 'Save & Continue';
    this.userStatusMessage = 'Enter details of Template and Click "Save & Continue" button to Select Template Modules. Template Status will by default be ACTIVE';
    this.slideStep1Div = true;
    this.slideStep2Div = false;
    this.stepNumber = 1;
    this.step3Flag = false;
    this.rowPrimaryKey = 0;
    this.loadNewTemplateScreenData();
  }

  private loadNewTemplateScreenData(): void {
    this.loading = true;
    this.cdr.detectChanges();

    const clientKey = this.template_data.client_key ?? this.clientKey;

    forkJoin({
      types: this.templatesService.getActiveTemplateTypes().pipe(
        catchError(() => of(null))
      ),
      jurisdictions: this.templatesService.getJurisdictionsByClient(clientKey).pipe(
        catchError(() => of(null))
      ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ types, jurisdictions }) => {
          this.loading = false;

          if (this.isCallSuccess(types) && types?.jsonObject?.length) {
            this.templateTypes = this.jsonObjFactory.arrObjToLower(types.jsonObject);
            if (this.templateTypes.length === 1) {
              this.template_data.template_type_key = this.templateTypes[0].template_type_key;
              this.template_data.template_type_name = this.templateTypes[0].template_type_name;
            }
          } else {
            this.snackbar.openSnackBar('No Template Types found. Create Template Types first.', 'gtw-snackbar--yellow');
            this.dialogRef?.close();
            this.cdr.detectChanges();
            return;
          }

          if (this.isCallSuccess(jurisdictions) && jurisdictions?.jsonObject?.length) {
            this.clientJurisdictions = this.jsonObjFactory.arrObjToLower(jurisdictions.jsonObject);
            const us = this.clientJurisdictions.find((j: any) => Number(j.jurisdictionkey) === 1);
            this.template_data.jurisdictionkey = us?.jurisdictionkey
              ?? this.clientJurisdictions[0]?.jurisdictionkey
              ?? 1;
          } else {
            this.template_data.jurisdictionkey = 1;
          }

          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.snackbar.openSnackBar('Error loading template data', 'gtw-snackbar--red');
          this.dialogRef?.close();
          this.cdr.detectChanges();
        },
      });
  }

  private isCallSuccess(res: any): boolean {
    const success = res?.callSuccess ?? res?.['callSuccess '];
    return String(success) === '1';
  }

  private initEditMode(templateKey: any): void {
    this.screenModeNew = false;
    this.mode = 'edit';
    this.title = 'Update Template';
    this.step1Title = 'Edit Template';
    this.step2Title = 'Edit Assigned Modules';
    this.saveLabel = 'Save & Continue';
    this.userStatusMessage = 'Step#1 Modify Template details and Click "Save & Continue" button to modify Template Modules';
    this.slideStep1Div = true;
    this.slideStep2Div = false;
    this.stepNumber = 1;
    this.step3Flag = false;
    this.rowPrimaryKey = templateKey;
    this.loadTemplateDetail(templateKey);
  }

  private loadTemplateGroups(): void {
    const user = this.appService.USER_SETTINGS?.user;
    const ck = user?.client?.client_key ?? user?.client_key;
    if (ck == null || ck === '') return;

    this.adminFactory.getAppKeys(ck)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          const channelList = data?.channel?.p_cursor;
          if (channelList?.length) {
            const channelKey = channelList[0].channel_key;
            this.adminFactory.getConfigSettings(channelKey)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (configData: any) => {
                  const tgObj = (configData?.configSettings || [])
                    .find((s: any) => s.NAME === 'access_template_groups');
                  if (tgObj?.VALUE) {
                    try { this.templateGroups = safeJsonInput<any>(tgObj.VALUE, null, 'template-edit.parse1'); } catch { this.templateGroups = {}; }
                  }
                  if (this.mode === 'new' && !this.template_data.template_group) {
                    const firstKey = Object.keys(this.templateGroups || {})[0];
                    if (firstKey) {
                      this.template_data.template_group = this.templateGroups[firstKey];
                    }
                  } else {
                    this.syncTemplateGroupSelection();
                  }
                  this.cdr.detectChanges();
                },
              });
          }
        },
      });
  }

  onTemplateTypeChange(typeKey: any): void {
    const matched = (this.templateTypes || []).find(
      (t: any) => String(t.template_type_key) === String(typeKey)
    );
    this.template_data.template_type_name = matched?.template_type_name || '';
  }

  private loadTemplateDetail(templateKey: any): void {
    this.loading = true;
    this.cdr.detectChanges();

    this.templatesService.getTemplateDetail(templateKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loading = false;
          if (this.isCallSuccess(res) && res?.jsonObject?.[0]) {
            const raw = this.jsonObjFactory.arrObjToLower(res.jsonObject);
            this.template_data = raw[0];
            if (this.template_data.template_group == null && this.template_data.template_group_key != null) {
              this.template_data.template_group = this.template_data.template_group_key;
            }
            this.syncTemplateGroupSelection();
            this.initClientFromLoadedData(res.jsonObject[0].CLIENT_KEY);
          } else {
            this.snackbar.openSnackBar('Error loading template data', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.snackbar.openSnackBar('Error loading template data', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private initClientFromLoadedData(loadedClientKey: any): void {
    const user = this.appService.USER_SETTINGS?.user;
    if (!user) return;
    this.clients = user.clients || [];
    this.template_data.client_key = loadedClientKey ?? user.client_key;
    const match = this.clients.find((c: any) => c.client_key === loadedClientKey);
    this.selectedClientName = match?.client_name || user.client?.client_name || '';
  }

  // ─── Template Group helpers ───────────────────────────────────────

  get templateGroupKeys(): string[] {
    return Object.keys(this.templateGroups || {});
  }

  compareTemplateGroup = (a: any, b: any): boolean => String(a) === String(b);

  /** Align ngModel with option values; handles numeric/string keys and legacy name-only values. */
  private syncTemplateGroupSelection(): void {
    const raw = this.template_data.template_group ?? this.template_data.template_group_key;
    if (raw == null || raw === '') {
      return;
    }

    for (const name of Object.keys(this.templateGroups || {})) {
      const value = this.templateGroups[name];
      if (String(value) === String(raw)) {
        this.template_data.template_group = value;
        return;
      }
    }

    if (this.templateGroups[raw] != null) {
      this.template_data.template_group = this.templateGroups[raw];
    }
  }

  private resolveTemplateGroupKeyForSave(): any {
    this.syncTemplateGroupSelection();
    return this.template_data.template_group ?? this.template_data.template_group_key ?? '';
  }

  // ─── Template Status toggle ───────────────────────────────────────

  get isActive(): boolean {
    return this.template_data.template_active === 1;
  }

  toggleStatus(): void {
    this.template_data.template_active = this.template_data.template_active === 1 ? 0 : 1;
  }

  // ─── Step Navigation ──────────────────────────────────────────────

  get step1Class(): string {
    if (this.stepNumber === 1) return 'step-active';
    return 'step-done';
  }

  get step2Class(): string {
    if (this.stepNumber < 2) return 'step-muted';
    if (this.stepNumber === 2) return 'step-active';
    return 'step-done';
  }

  get step3Class(): string {
    return this.step3Flag ? 'step-active' : 'step-muted';
  }

  previousScreen(): void {
    if (this.mode === 'new') {
      this.initEditMode(this.rowPrimaryKey);
    } else {
      this.initEditMode(this.rowData.TEMPLATE_KEY);
    }
  }

  // ─── Save ─────────────────────────────────────────────────────────

  save(): void {
    if (this.saveLabel === 'Close') {
      this.dialogRef?.close('saved');
      return;
    }

    if (this.saveLabel === 'Continue') {
      this.advanceStep();
      return;
    }

    this.submitted = true;
    this.cdr.detectChanges();

    if (this.crudLoading) {
      this.snackbar.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    if (!this.template_data.template_name?.trim()) {
      this.snackbar.openSnackBar('Template Name is required', 'gtw-snackbar--yellow');
      return;
    }

    const templateGroupKey = this.resolveTemplateGroupKeyForSave();
    if (templateGroupKey == null || templateGroupKey === '') {
      this.snackbar.openSnackBar('Template Group is required', 'gtw-snackbar--yellow');
      return;
    }

    if (this.stepNumber !== 1) return;

    if (!this.ensureTemplateTypeForSave()) {
      return;
    }

    this.crudLoading = true;
    this.cdr.detectChanges();

    let payload: any[];
    if (this.mode === 'new') {
      payload = [{
        TEMPLATE_NAME: this.template_data.template_name,
        TEMPLATE_DESCRIPTION: this.template_data.template_description || '',
        TEMPLATE_GROUP_KEY: templateGroupKey,
        TEMPLATE_TYPE_KEY: this.template_data.template_type_key || '',
        TEMPLATE_TYPE_NAME: this.template_data.template_type_name || '',
        TEMPLATE_TYPE_DESC: this.template_data.template_type_desc || '',
        CLIENT_KEY: this.template_data.client_key ?? this.clientKey,
        JURISDICTION_KEY: this.template_data.jurisdictionkey || 1,
      }];
    } else {
      payload = [{
        TEMPLATE_KEY: this.template_data.template_key,
        TEMPLATE_NAME: this.template_data.template_name,
        TEMPLATE_DESCRIPTION: this.template_data.template_description || '',
        TEMPLATE_GROUP_KEY: templateGroupKey,
        TEMPLATE_ACTIVE: this.template_data.template_active,
        TEMPLATE_TYPE_KEY: this.template_data.template_type_key || '',
        TEMPLATE_TYPE_NAME: this.template_data.template_type_name || '',
        TEMPLATE_TYPE_DESC: this.template_data.template_type_desc || '',
        CLIENT_KEY: this.template_data.client_key ?? this.clientKey,
        JURISDICTION_KEY: this.template_data.jurisdictionkey || 1,
      }];
    }

    this.templatesService.saveTemplate(payload, this.mode === 'edit')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.crudLoading = false;
          if (this.isCallSuccess(res)) {
            if (this.mode === 'new') {
              this.rowPrimaryKey = res.generatedPk;
            } else {
              this.rowPrimaryKey = this.template_data.template_key;
            }
            this.advanceStep();
          } else {
            this.snackbar.openSnackBar(res?.errorMessage || 'Save failed', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.crudLoading = false;
          this.snackbar.openSnackBar('Error saving template', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private ensureTemplateTypeForSave(): boolean {
    if (this.mode !== 'new') {
      return true;
    }

    if (!this.template_data.template_type_key && this.templateTypes.length === 1) {
      this.template_data.template_type_key = this.templateTypes[0].template_type_key;
      this.template_data.template_type_name = this.templateTypes[0].template_type_name;
    }

    if (!this.template_data.template_type_key) {
      this.snackbar.openSnackBar('Template Type is required', 'gtw-snackbar--yellow');
      return false;
    }

    const matched = (this.templateTypes || []).find(
      (t: any) => String(t.template_type_key) === String(this.template_data.template_type_key)
    );
    this.template_data.template_type_name = matched?.template_type_name || this.template_data.template_type_name || '';
    this.template_data.template_type_desc = this.template_data.template_type_desc || '';
    return true;
  }

  private advanceStep(): void {
    if (this.mode === 'new' && this.stepNumber === 1) {
      this.step1Title = 'Template Created';
      this.step2Title = 'Assign Modules';
      this.saveLabel = 'Continue';
      this.userStatusMessage = 'Using dropdown menu, Assign Roles for Each Activity. This automatically saves the Role with in the Template.';
      this.slideStep1Div = false;
      this.slideStep2Div = true;
      this.stepNumber = 2;
      this.loadModules();
    } else if (this.mode === 'edit' && this.stepNumber === 1) {
      this.step1Title = 'Template Updated';
      this.step2Title = 'Edit Template Modules';
      this.saveLabel = 'Continue';
      this.userStatusMessage = 'Using dropdown menu, change Role assignments for Each Activity. This automatically saves the Role with in the Template.';
      this.slideStep1Div = false;
      this.slideStep2Div = true;
      this.stepNumber = 2;
      this.loadModules();
    } else if (this.stepNumber === 2) {
      this.step1Title = this.mode === 'new' ? 'Template Created' : 'Template Updated';
      this.step2Title = this.mode === 'new' ? 'Template Modules Created' : 'Template Modules Updated';
      this.saveLabel = 'Close';
      this.userStatusMessage = this.mode === 'new'
        ? 'New Template created successfully. Click CLOSE button to close this window'
        : 'Template Edited successfully. Click CLOSE button to close this window';
      this.step3Flag = true;
      this.slideStep1Div = false;
      this.slideStep2Div = false;
      this.stepNumber = 3;
    }
    this.crudLoading = false;
    this.cdr.detectChanges();
  }

  // ─── Step 2: Module Assignment ────────────────────────────────────

  private loadModules(): void {
    this.loading = true;
    this.cdr.detectChanges();

    this.templatesService.getGtwModules({
      jurisdiction_key: 1,
      client_key: this.template_data.client_key ?? this.clientKey,
      row_primary_key: this.rowPrimaryKey,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loading = false;
          if (this.isCallSuccess(res) && res?.jsonObject) {
            this.userAssignNav = res.jsonObject;
            this.mergeNonMatchedTaskHierarchy(this.userAssignNav);
            this.initDefaultRoles(this.userAssignNav);
            this.initDefaultCollapsed(this.userAssignNav);
            this.cacheNodeMetadata(this.userAssignNav);
            this.updateModuleCounts();
            this.countTotalModules();

            const defaultApp = this.userAssignNav.find((n: any) => n.id === '5') || this.userAssignNav[0];
            if (defaultApp) {
              this.selectApplication(defaultApp);
            } else {
              this.filteredUserAssignNav = this.userAssignNav;
              this.distributeToColumns();
              this.refreshExpandedState();
            }
          } else {
            this.snackbar.openSnackBar('Error loading modules', 'gtw-snackbar--red');
          }
          this.loadModuleCount();
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.snackbar.openSnackBar('Error loading modules', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private loadModuleCount(): void {
    this.templatesService.getTemplateModuleCount({
      template_key: this.rowPrimaryKey,
      logged_in_user_sso_id: this.userId,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.[0]?.moduleCount != null) {
            this.totalModulesAssigned = res[0].moduleCount;
          }
          this.cdr.detectChanges();
        },
      });
  }

  selectApplication(item: any): void {
    this.searchTerm = '';
    this.selectedApplication = item;
    this.filteredUserAssignNav = item?.children || [];
    this.distributeToColumns();
    this.refreshExpandedState();
    this.cdr.detectChanges();
  }

  filterMenu(term: string): void {
    this.searchTerm = term || '';
    if (this.selectedApplication?.children) {
      this.filteredUserAssignNav = this.filterTree([...this.selectedApplication.children], this.searchTerm);
    }
    this.distributeToColumns();
    this.refreshExpandedState();
    this.cdr.detectChanges();
  }

  private distributeToColumns(): void {
    const columns: any[][] = [[], []];
    (this.filteredUserAssignNav || []).forEach((node, index) => {
      columns[index % 2].push(node);
    });
    this.moduleColumns = columns;
  }

  private refreshExpandedState(): void {
    this.allExpanded = (this.filteredUserAssignNav || []).some((node: any) => !node.collapsed);
  }

  trackByNodeId(_index: number, node: any): any {
    return node?.id ?? node?.jcd_ta_key ?? _index;
  }

  trackByIndex(index: number): number {
    return index;
  }

  toggleAll(): void {
    const shouldCollapse = this.allExpanded;
    (this.filteredUserAssignNav || []).forEach((node: any) => {
      node.collapsed = shouldCollapse;
    });
    this.allExpanded = !shouldCollapse;
    this.cdr.detectChanges();
  }

  toggleNode(node: any): void {
    node.collapsed = !node.collapsed;
    this.refreshExpandedState();
    this.cdr.detectChanges();
  }

  openUserAccessModal(node: any): void {
    const dialogRef = this.dialog.open(UserAssignModalComponent, {
      width: '600px',
      disableClose: true,
      panelClass: ['assign-user-dialog', 'increase-z-index'],
      position: { top: '25px' },
      data: { taskKey: node.id },
    });
    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (result?.saved) {
          this.cacheNodeMetadata(this.userAssignNav);
        }
        this.cdr.detectChanges();
      });
  }

  onNodeRoleValueChange(node: any, value: any): void {
    const role = this.findRoleByValue(node, value);
    if (!role) return;
    node.role = role;
    this.updateModuleCounts();
    this.saveRoleChanges([node]);
  }

  applyGroupRole(mode: string, node: any): void {
    const changed: any[] = [];
    const walk = (n: any): void => {
      if (!n) return;
      if (Array.isArray(n.roles) && n.roles.length > 0) {
        const nextRole = this.resolveRoleByMode(n, mode);
        if (nextRole) {
          n.role = nextRole;
          changed.push(n);
        }
      }
      if (Array.isArray(n.children)) {
        n.children.forEach((child: any) => walk(child));
      }
    };
    walk(node);
    this.updateModuleCounts();
    this.saveRoleChanges(changed);
  }

  updateModuleCounts(): void {
    for (const app of this.userAssignNav) {
      this.computeCounts(app);
    }
    this.cdr.detectChanges();
  }

  private saveRoleChanges(changedNodes: any[]): void {
    if (!this.rowPrimaryKey || !changedNodes?.length) return;
    const payload = this.mapNodesToRolePayload(changedNodes);
    if (payload.length === 0) return;

    this.templatesService.setModuleAccess(payload, this.rowPrimaryKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (this.isCallSuccess(res)) {
            this.loadModuleCount();
          }
          this.cdr.detectChanges();
        },
      });
  }

  private mapNodesToRolePayload(nodes: any[]): any[] {
    const clientKey = this.template_data.client_key ?? this.clientKey;
    return (nodes || [])
      .filter((node: any) => !!node?.jcd_ta_key && node?.role?.value !== undefined && node?.role?.value !== null)
      .map((node: any) => ({
        jcd_ta_key: node.jcd_ta_key,
        jcd_ta_role_key: node.role.value,
        user_id: this.userId,
        client_key: clientKey,
      }));
  }

  private findRoleByValue(node: any, value: any): any {
    if (!Array.isArray(node?.roles)) return null;
    return node.roles.find((role: any) => String(role?.value) === String(value)) || null;
  }

  private resolveRoleByMode(node: any, mode: string): any {
    if (!Array.isArray(node?.roles) || node.roles.length === 0) return null;
    if (mode === '1') {
      const highest = Math.max(...node.roles.map((r: any) => Number(r.level ?? 0)));
      return node.roles.find((r: any) => Number(r.level ?? 0) === highest) || null;
    }
    if (mode === '-1') {
      const valid = node.roles.map((r: any) => Number(r.level ?? 0)).filter((level: number) => level !== 0);
      if (valid.length === 0) {
        return node.roles.find((r: any) => Number(r.level ?? 0) === 0) || null;
      }
      const lowest = Math.min(...valid);
      return node.roles.find((r: any) => Number(r.level ?? 0) === lowest) || null;
    }
    return node.roles.find((r: any) => Number(r.level ?? 0) === 0) || node.roles[0];
  }

  private filterTree(nodes: any[], term: string): any[] {
    const t = (term || '').toLowerCase();
    if (!t) return nodes || [];
    return (nodes || []).filter((item: any) => {
      if (Array.isArray(item.children)) {
        item.children = this.filterTree(item.children, term);
      }
      const name = String(item?.name || item?.task_name || '').toLowerCase();
      return name.includes(t) || (item.children && item.children.length > 0);
    });
  }

  private cacheNodeMetadata(nodes: any[]): void {
    for (const node of nodes) {
      node._tooltip = (node.ownerList?.length > 0)
        ? node.ownerList.map((u: any) => u.name).join(', ')
        : 'No Users Assigned';
      if (node.children) {
        this.cacheNodeMetadata(node.children);
      }
    }
  }

  private initDefaultRoles(nodes: any[]): void {
    for (const node of nodes) {
      if (node.roles?.length > 0) {
        if (!node.role) {
          node.role = node.roles[0];
        } else {
          const match = node.roles.find((r: any) => String(r.value) === String(node.role.value));
          if (match) node.role = match;
        }
      }
      if (node.children) {
        this.initDefaultRoles(node.children);
      }
    }
  }

  private initDefaultCollapsed(nodes: any[]): void {
    for (const node of nodes) {
      node.collapsed = true;
      node._bulkDropdown = '';
      if (node.children) {
        this.initDefaultCollapsed(node.children);
      }
    }
  }

  private computeCounts(node: any): { assigned: number; total: number } {
    if (!node.children || node.children.length === 0) {
      node._assigned = 0;
      node._total = 0;
      node._badgeClass = 'badge-partial';
      return { assigned: 0, total: 0 };
    }

    let assigned = 0;
    let total = 0;

    for (const child of node.children) {
      if (child?.role && child.role.value > 0) assigned++;
      if (child?.role) total++;
      if (child?.children?.length > 0) {
        const childCounts = this.computeCounts(child);
        assigned += childCounts.assigned;
        total += childCounts.total;
      } else if (child) {
        child._assigned = 0;
        child._total = 0;
        child._badgeClass = 'badge-partial';
      }
    }

    node._assigned = assigned;
    node._total = total;
    node._badgeClass = (assigned === total && total > 0) ? 'badge-complete' : 'badge-partial';
    return { assigned, total };
  }

  private countTotalModules(): void {
    let count = 0;
    const walk = (list: any[]) => {
      (list || []).forEach((item: any) => {
        if (String(item?.type || '').toLowerCase() === 'activity') count++;
        if (Array.isArray(item?.children)) walk(item.children);
      });
    };
    walk(this.userAssignNav || []);
    this.totalModulesForSelectedClient = count;
  }

  private mergeNonMatchedTaskHierarchy(jsonAry: any[]): void {
    for (const chlObj of jsonAry) {
      if (chlObj.children?.length) {
        for (const childTask of chlObj.children) {
          const childMatches = chlObj.children.filter(
            (c: any) => c.parentTaskKey === childTask.id
          );
          if (childMatches.length) {
            (childTask as any).processed = true;
            for (const cm of childMatches) {
              if (childTask.children) {
                childTask.children.unshift(cm);
              } else {
                childTask.children = [cm];
              }
            }
          }
        }
        chlObj.children = chlObj.children.filter(
          (c: any) => c.parentTaskKey == null
        );
      }
    }
  }

  cancel(): void {
    this.dialogRef?.close();
  }
}
